import { xano } from "@/lib/xano";
import { EngineView } from "@/components/EngineView";
import { StatCard, SectionTitle, DataTable } from "@/components/shared";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle } from "lucide-react";
import { gbp, num, dateUk } from "@/lib/format";
import type { VatPosition } from "@/lib/engine-types";
import { VatToolsSection } from "@/components/VatToolsSection";

function render(raw: unknown) {
  const d = raw as VatPosition;
  return (
    <>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Net sales (period)" value={gbp(d.period?.net)} hint={`${num(d.period?.orders)} orders`} />
        <StatCard label="UK output VAT" value={gbp(d.uk_vat?.output_vat)} hint="Box 1" />
        <StatCard
          label="EU VAT to account"
          value={gbp(d.eu?.vat_to_account)}
          tone={d.eu?.vat_to_account > 0 ? "warning" : "default"}
          hint={`${num(d.eu?.country_count)} countries`}
        />
        <StatCard
          label="Miscoded orders"
          value={num(d.miscoded?.count)}
          tone={d.miscoded?.count > 0 ? "destructive" : "success"}
          hint={d.miscoded?.count > 0 ? "Need correction" : "All clean"}
        />
      </div>

      <SectionTitle>Sales by country</SectionTitle>
      <DataTable
        columns={[
          { key: "name", label: "Country" },
          { key: "region", label: "Region" },
          { key: "orders", label: "Orders", align: "right" },
          { key: "net", label: "Net sales", align: "right" },
          { key: "vat", label: "VAT collected", align: "right" },
        ]}
        rows={(d.by_country ?? []).map((c) => ({
          name: `${c.name}`,
          region: <Badge variant="secondary">{c.region}</Badge>,
          orders: num(c.orders),
          net: gbp(c.net_sales, { decimals: true }),
          vat: gbp(c.vat_collected, { decimals: true }),
        }))}
      />

      <SectionTitle>EU VAT to account for</SectionTitle>
      <Card className="mb-3">
        <CardContent className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-4">
          <Mini label="EU net sales" value={gbp(d.eu?.net_sales)} />
          <Mini label="IOSS eligible (≤ €150)" value={gbp(d.eu?.ioss_eligible_net)} />
          <Mini label="Over threshold" value={gbp(d.eu?.over_threshold_net)} />
          <Mini label="IOSS due" value={d.eu?.ioss_due_date ? dateUk(d.eu.ioss_due_date) : "—"} />
        </CardContent>
      </Card>
      <DataTable
        columns={[
          { key: "name", label: "Country" },
          { key: "rate", label: "VAT rate", align: "right" },
          { key: "orders", label: "Orders", align: "right" },
          { key: "net", label: "Net sales", align: "right" },
          { key: "owed", label: "VAT owed", align: "right" },
        ]}
        rows={(d.eu?.by_country ?? []).map((c) => ({
          name: c.name,
          rate: `${c.vat_rate}%`,
          orders: num(c.orders),
          net: gbp(c.net_sales, { decimals: true }),
          owed: <span className="font-medium">{gbp(c.vat_owed, { decimals: true })}</span>,
        }))}
      />

      {d.miscoded?.count > 0 && (
        <Card className="mt-4 border-destructive/30 bg-destructive/5">
          <CardContent className="flex items-start gap-3 p-5 text-sm">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div>
              <div className="font-medium text-destructive">
                {num(d.miscoded.count)} miscoded orders, {gbp(d.miscoded.total_tax_charged, { decimals: true })} tax charged
              </div>
              <p className="mt-1 text-muted-foreground">
                These orders were taxed incorrectly at checkout. Fix them with correction journals
                in the Books tab before your next return.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <VatToolsSection />
    </>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-semibold tabular-nums">{value}</div>
    </div>
  );
}

export default function TaxVat() {
  return (
    <EngineView
      title="Tax & VAT"
      description="Your VAT position by country and period: UK output VAT, EU VAT to account for, IOSS and thresholds."
      queryKey="vat-position"
      queryFn={(token) => xano.dashboard.vatPosition(token)}
      render={render}
    />
  );
}
